import { toast } from 'react-toastify';
import API_BASE_URL from 'src/constants';
import i18n from 'src/localization/i18n';
import { paths } from 'src/generated/v1';
import logger from 'src/utils/logger';
import { $api, $apiFetchClient } from './api';
import { handleUnauthorized } from './authToken';

export enum MimeType {
  PLAIN = 'text/plain',
  HTML = 'text/html',
  CSS = 'text/css',
  JS = 'text/javascript',
  JSON = 'application/json',
  XML = 'application/xml',
  PDF = 'application/pdf',
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  SVG = 'image/svg+xml',
  MPEG = 'audio/mpeg',
  MP4 = 'video/mp4',
  ZIP = 'application/zip',
}

export type AllowedModel = 'product.product' | 'sale.order' | 'purchase.order';

/**
 * Function to delete a utility attachment by ID
 * @param id  - ID of the utility attachment
 * @returns boolean
 */
export const deleteUtilityAttachmentById = async (id: number): Promise<boolean> => {
  logger.info('Deleting attachment', id);
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    toast.error(i18n.t('errors.login_again'));
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}portal/utility/attachments/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorMessage = await response.text();
      toast.error(`${i18n.t('errors.delete_attachment_failed')}: ${errorMessage}`);
      return false;
    }

    toast.success(i18n.t('success.attachment_deleted'));
    return true;
  } catch (err: unknown) {
    logger.error('Failed to delete attachment', err);
    if (err instanceof Error) {
      toast.error(`${i18n.t('errors.delete_attachment_failed')}: ${err.message}`);
    } else {
      toast.error(i18n.t('errors.delete_attachment_failed'));
    }
    return false;
  }
};

// Custom hook to use the deleteUtilityAttachmentById function
export const useDeleteUtilityAttachmentById = () => {
  return (id: number) => deleteUtilityAttachmentById(id);
};

export const getUtilityModels = async () => {
  const accessToken = localStorage.getItem('access_token');
  try {
    const response = await fetch(`${API_BASE_URL}portal/utility/models/model`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized(accessToken);
      }
      throw new Error(`Failed to fetch models: ${response.statusText}`);
    }

    return await response.json();
  } catch (err: unknown) {
    logger.error('Failed to fetch models', err);
    return false;
  }
};

export const getImportTemplate = async (model: AllowedModel) => {
  const resp = await $apiFetchClient.GET('/utility/bulk_action/{model}/get_import_template', {
    params: {
      path: {
        model,
      },
    },
    parseAs: 'blob',
  });
  return resp.data;
};

export const useDescriptionsQuery = (model_name: string) => {
  return $api.useQuery('get', '/utility/descriptions/{model_name}', {
    params: { path: { model_name } },
  });
};

export const getLangCodes = async () => {
  const response = await $apiFetchClient.GET('/utility/lang_codes', {
    params: {},
  });
  return response;
};

export const uploadImportTemplate = async (model: AllowedModel, file: File): Promise<boolean> => {
  logger.info('Uploading import template', model);
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await $apiFetchClient.POST('/utility/bulk_action/{model}/import_records', {
      params: {
        path: {
          model,
        },
      },
      body: formData as unknown as { file: string },
    });
    if (response.error) {
      toast.error(`${i18n.t('notifications.import_failed')} ${response.error.detail}`, { autoClose: 5000 });
      return false;
    }
    toast.success(i18n.t('notifications.import_success'));
    return true;
  } catch (err) {
    logger.error('Import failed', err);
    if (err instanceof Error) {
      toast.error(`${i18n.t('notifications.import_failed')} ${err}`, { autoClose: 5000 });
    }
    return false;
  }
};

/**
 * download the attachment by id and create an a tag to download it via the browser
 * @param attachment_id the id
 * @returns boolean
 */
export const downloadAttachment = async (attachment_id: number) => {
  logger.info('Downloading attachment', attachment_id);
  const attachmentUrl = `${API_BASE_URL}portal/utility/attachments/${attachment_id}`;
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    toast.error(i18n.t('errors.login_again'));
    return false;
  }

  try {
    const response = await fetch(attachmentUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `attachment_${attachment_id}`); // You can customize the filename here
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    return true;
  } catch (error) {
    logger.error('Failed to download attachment', error);
    return false;
  }
};

export const validateBarcode = async (barcode: string) => {
  const apiClient = $apiFetchClient;

  const { data } = await apiClient.GET('/utility/barcodes/{barcode}', {
    params: {
      path: {
        barcode,
      },
    },
  });

  return data;
};

export const useBadgesQuery = () => {
  return $api.useQuery('get', '/utility/badges/');
};

export const useImageQuery = (imageId: number) => {
  return $api.useQuery('get', '/utility/images/image/{image_id}', {
    params: { path: { image_id: imageId } },
  });
};

export const useBarcodeValidationQuery = (barcode: string, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    '/utility/barcodes/{barcode}',
    {
      params: {
        path: {
          barcode,
        },
      },
    },
    options,
  );
};

export const useQuickFilterQuery = (
  model: paths['/utility/models/quick_filter/{model}']['get']['parameters']['path']['model'],
) => {
  // Tab labels are translated in the backend (df.quick.filter.state.label is
  // translatable and follows the user's language); render as-is.
  return $api.useQuery('get', '/utility/models/quick_filter/{model}', {
    params: {
      path: {
        model,
      },
    },
  });
};

export const useCountriesQuery = () => {
  return $api.useQuery('get', '/utility/countries');
};
